import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedDate } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { sortByDate } from '@sorare/core/src/lib/dates';
import { isType } from '@sorare/core/src/lib/gql';
import { teamPictureUrl } from '@sorare/core/src/lib/so5';

import {
  isGameLive,
  isGameScheduled,
} from '@sorare/marketplace/src/shared/game/lib';

import type { So5AppearenceInlineGamesDots_so5Appearance } from './__generated__/index.graphql';

const Wrapper = styled(Horizontal)`
  --size-unit: 1;

  gap: 1px;
  color: var(--c-nd-400);
  font: var(--t-label-xs);
  max-width: 100%;
`;
const PropablePitcher = styled.span`
  color: var(--c-onBase-success);
  border: 1px solid var(--c-onBase-success);
  border-radius: 50%;
  padding: calc(var(--size-unit) * 2px);
  font-size: calc(var(--size-unit) * 8px);
`;

const EmptyCircle = styled.span`
  font-size: calc(var(--size-unit) * 10px);
  color: var(--c-nd-800);
`;

const FullCircle = styled.span`
  font-size: calc(var(--size-unit) * 9px);
  color: var(--c-onBase-success);
`;

const Cross = styled.span`
  font-size: calc(var(--size-unit) * 10px);
  color: rgba(157, 61, 50, 0.85);
`;

const TeamLogo = styled.img`
  width: 1.2em;
  height: 1.2em;
  object-fit: contain;
  border-radius: var(--half-unit);
`;

const ReversibleHorizontal = styled(Horizontal)`
  &.reversed {
    flex-direction: row-reverse;
  }
`;

const TooltipContent = styled(Vertical)`
  color: var(--c-nd-600);
  font: var(--t-label-s);
`;

const Won = styled.span`
  color: var(--c-white);
  font-weight: var(--t-bold);
`;

const Lost = styled.span`
  color: var(--c-nd-600);
`;

const HOVER_DELAY = 300;

type Props = {
  so5Appearance: So5AppearenceInlineGamesDots_so5Appearance;
  sizeUnit?: 'sm' | 'md' | 'lg' | 'xl';
  isStartingPitcher?: boolean;
  pitchingGames?: So5AppearenceInlineGamesDots_so5Appearance['eligiblePlayerGameScores'];
};

const sizeUnitsMap = {
  sm: 0.9,
  md: 1.1,
  lg: 1.5,
  xl: 1.8,
};

export const So5AppearenceInlineGamesDots = ({
  so5Appearance,
  sizeUnit = 'md',
  isStartingPitcher,
  pitchingGames = [],
}: Props) => {
  const { eligiblePlayerGameScores, playedGames } = so5Appearance;

  const sortedGames = sortByDate(
    [...eligiblePlayerGameScores],
    playerGameScore => playerGameScore.anyGame.date
  );

  return (
    <Wrapper style={{ '--size-unit': sizeUnitsMap[sizeUnit] } as CSSProperties}>
      {sortedGames.map(pgs => {
        const DNP = <Cross>✕</Cross>;
        const hasPlayed = playedGames.some(g => g.id === pgs.anyGame.id);
        let status: ReactNode = hasPlayed ? <FullCircle>●</FullCircle> : DNP;

        if (isGameLive(pgs.anyGame.statusTyped)) {
          status = (
            <LiveDot animate size="md" color="var(--c-onBase-success)" />
          );
        }
        if (isGameScheduled(pgs.anyGame.statusTyped)) {
          status = <EmptyCircle>○</EmptyCircle>;
        }

        if (isStartingPitcher) {
          const isPlayerPitchingThisGame =
            pitchingGames[0]?.anyGame.id === pgs.anyGame.id;

          status = isPlayerPitchingThisGame ? <FullCircle>●</FullCircle> : DNP;

          if (isGameLive(pgs.anyGame.statusTyped)) {
            status = isPlayerPitchingThisGame ? (
              <LiveDot animate size="md" color="var(--c-onBase-success)" />
            ) : (
              DNP
            );
          }
          if (isGameScheduled(pgs.anyGame.statusTyped)) {
            status = isPlayerPitchingThisGame ? (
              <PropablePitcher>PP</PropablePitcher>
            ) : (
              DNP
            );
          }
        }
        const game = pgs.anyGame;
        const isFutureGame = isGameScheduled(game.statusTyped);

        const reverseTeamOrder =
          isType(game, 'GameOfBaseball') || isType(game, 'GameOfBasketball');

        const homeTeamPictureUrl = game.homeTeam
          ? teamPictureUrl(game.homeTeam)
          : null;
        const awayTeamPictureUrl = game.awayTeam
          ? teamPictureUrl(game.awayTeam)
          : null;

        const homeScore = game.homeScore ?? '';
        const awayScore = game.awayScore ?? '';

        const HomeScore = homeScore >= awayScore ? Won : Lost;
        const AwayScore = homeScore <= awayScore ? Won : Lost;

        return (
          <span key={pgs.anyGame.id}>
            <Tooltip
              hoverOpenDelay={HOVER_DELAY}
              title={
                <TooltipContent center>
                  <ReversibleHorizontal
                    className={classNames({ reversed: reverseTeamOrder })}
                  >
                    <span>{pgs.anyGame.homeTeam?.code}</span>
                    {!!homeTeamPictureUrl && (
                      <TeamLogo src={homeTeamPictureUrl} alt="" />
                    )}
                    {isFutureGame ? (
                      <Lost>-</Lost>
                    ) : (
                      <>
                        <HomeScore>{homeScore}</HomeScore> <Lost>-</Lost>
                        <AwayScore>{awayScore}</AwayScore>
                      </>
                    )}
                    {!!awayTeamPictureUrl && (
                      <TeamLogo src={awayTeamPictureUrl} alt="" />
                    )}
                    <span>{game.awayTeam?.code}</span>
                  </ReversibleHorizontal>
                  {!!pgs.anyGame.date && (
                    <FormattedDate
                      value={pgs.anyGame.date}
                      month="short"
                      day="numeric"
                      hour="2-digit"
                      minute="2-digit"
                    />
                  )}
                </TooltipContent>
              }
            >
              {status}
            </Tooltip>
          </span>
        );
      })}
    </Wrapper>
  );
};

So5AppearenceInlineGamesDots.fragments = {
  so5Appearance: gql`
    fragment So5AppearenceInlineGamesDots_so5Appearance on So5AppearanceInterface {
      id
      scoreStatus
      eligiblePlayerGameScores {
        id
        anyGame {
          id
          statusTyped
          date
          homeScore
          awayScore
          homeTeam {
            slug
            code
            ...teamPictureUrl_teamInterface
          }
          awayTeam {
            slug
            code
            ...teamPictureUrl_teamInterface
          }
        }
      }
      playedGames {
        id
      }
    }
    ${teamPictureUrl.fragments.teamInterface}
  ` as TypedDocumentNode<So5AppearenceInlineGamesDots_so5Appearance>,
};
